import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../services/api";
import "./PastPayroll.css";

const PastPayroll = () => {
    const { id: businessId } = useParams();
    const navigate = useNavigate();
    const [payPeriodStartDate, setPayPeriodStartDate] = useState("");
    const [payPeriodEndDate, setPayPeriodEndDate] = useState("");
    const [quarterRanges, setQuarterRanges] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [expandedQuarters, setExpandedQuarters] = useState({});
    const [businessInfo, setBusinessInfo] = useState({ business_state: "", b_county: "", local_city1: "" });

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toISOString().split("T")[0];
    };

    const handleFetchPayroll = async () => {
        if (!payPeriodStartDate || !payPeriodEndDate) {
            alert("Please enter both dates.");
            return;
        }

        try {
            const formattedStartDate = formatDate(payPeriodStartDate);
            const formattedEndDate = formatDate(payPeriodEndDate);

            const payload = {
                business_id: businessId,
                first_pay_period_start_date: formattedStartDate,
                last_pay_date: formattedEndDate,
            };

            const { data } = await api.post(`/past-payroll`, payload);
            setQuarterRanges(data.quarter_ranges);
            setEmployees(data.employees);

            // Store business info from response
        if (data.business_info) {
            setBusinessInfo({
                business_state: data.business_info.business_state,
                b_county: data.business_info.b_county,
                local_city1: data.business_info.local_city1,
            });
        }

        } catch (error) {
            console.error("Error fetching payroll information:", error);
            alert("Failed to fetch payroll data.");
        }
    };

   




    const handleSubmit = async () => {
        const inputs = employees.map((employee) => ({
            employee_id: `${employee.id}`,
            filing_status: `${employee.filing_status}`,
            e_state: `${employee.e_state}`,
            e_city: `${employee.e_city}`,
            e_county: `${employee.e_county}` || null,
            e_st_add: `${employee.e_st_add}`,
            e_zip: `${employee.e_zip}`,
            work_loc: `${employee.work_loc}`,
            b_county: `${businessInfo.b_county}` || null,
            business_state: `${businessInfo.business_state}`,
            name: `${employee.first_name} ${employee.last_name}`,
            quarters: employee.quarters.map((quarter, quarterIndex) => {
                const taxInputs = employee.tax_inputs.reduce((acc, field, fieldIndex) => {
                    const inputId = `tax-${employee.id}-${quarterIndex}-${fieldIndex}`;
                    const inputElement = document.getElementById(inputId);
                    const taxValue = inputElement && inputElement.value.trim() !== "" ? parseFloat(inputElement.value) : null;
    
                    switch (field.type) {
                        case "employee state":
                            acc.state_tax = taxValue;
                            break;
                        case "business state":
                            acc.work_state_tax = taxValue;
                            break;
                        case "employee county":
                            acc.county_resident = taxValue;
                            break;
                        case "business county":
                            acc.county_non_resident = taxValue;
                            break;
                        case "employee city EIT":
                            acc.resident_eit = taxValue;
                            break;
                        case "business city EIT":
                            acc.non_resident_eit = taxValue;
                            break;
                        case "employee city LST":
                            acc.lst_city = employee.e_city;
                            acc.lst_tax = taxValue;
                            break;
                        case "business city LST":
                            acc.lst_city = businessInfo.local_city1;
                            acc.lst_tax = taxValue;
                            break;
                        case "business city":
                            if (!acc.non_resident_eit) {  // 🛠 Avoid duplication of EIT as general tax
                                acc.local_city1 = businessInfo.local_city1;
                                acc.local_citytax1 = taxValue;
                            }
                            break;
                        case "employee city":
                            if (!acc.resident_eit) {  // 🛠 Avoid duplication of EIT as general tax
                                acc.local_city2 = employee.e_city;
                                acc.local_citytax2 = taxValue;
                            }
                            break;
                        default:
                            acc.otherTaxes.push({
                                label: field.label,
                                type: field.type,
                                value: taxValue,
                            });
                            break;
                    }
                    return acc;
                }, {
                    state_tax: null,
                    work_state_tax: null,
                    county_resident: null,
                    county_non_resident: null,
                    resident_eit: null,
                    non_resident_eit: null,
                    lst_city: null,
                    lst_tax: null,
                    local_citytax1: null,
                    local_citytax2: null,
                    otherTaxes: [],
                });
    
                const additionalInputs = [
                    "contrib_401k",
                    "vacation_hours",
                    "vacation_pay",
                    "healthcare",
                    "dental",
                    "vision",
                ].reduce((acc, key) => {
                    const inputId = `${key}-${employee.id}-${quarterIndex}`;
                    const inputElement = document.getElementById(inputId);
                    acc[key] = inputElement && inputElement.value.trim() !== "" ? parseFloat(inputElement.value) : null;
                    return acc;
                }, {});
    
                return {
                    quarter,
                    start_date: quarterRanges[quarterIndex]?.start || "",
                    end_date: quarterRanges[quarterIndex]?.end || "",
                    grosspay: parseFloat(document.getElementById(`grossPay-${employee.id}-${quarterIndex}`)?.value || "0"),
                    federal_tax: parseFloat(document.getElementById(`federalTax-${employee.id}-${quarterIndex}`)?.value || "0"),
                    state_tax: taxInputs.state_tax,
                    work_state_tax: taxInputs.work_state_tax,
                    county_resident: taxInputs.county_resident,
                    county_non_resident: taxInputs.county_non_resident,
                    resident_eit: taxInputs.resident_eit,
                    non_resident_eit: taxInputs.non_resident_eit,
                    lst_city: taxInputs.lst_city,
                    lst_tax: taxInputs.lst_tax,
                    local_citytax1: taxInputs.local_citytax1,
                    local_citytax2: taxInputs.local_citytax2,
                    taxInputs: taxInputs.otherTaxes, // Preserve other taxes if needed
                    ...additionalInputs,
                };
            }),
        }));
    
        try {
            const payload = {
                business_id: businessId,
                employees: inputs,
            };
    
            await api.post(`/add-past-data`, payload);
            alert("Data submitted successfully!");
            navigate(`/dashboard/business/${businessId}`);
        } catch (error) {
            console.error("Error submitting past data:", error);
            alert("Failed to submit data. Please try again.");
        }
    };
    
    







    const toggleQuarter = (employeeIndex, quarterIndex) => {
        setExpandedQuarters((prevState) => ({
            ...prevState,
            [`${employeeIndex}-${quarterIndex}`]: !prevState[`${employeeIndex}-${quarterIndex}`],
        }));
    };

    return (
        <div className="past-payroll-container">
            <h3 className="page-title-past">Add Previous Payroll</h3>
            <div className="pay-start">
                <label htmlFor="payPeriodStartDate" className="past-label">
                    First Pay Period Start Date of Current Year:
                </label>
                <input
                    id="payPeriodStartDate"
                    type="date"
                    value={payPeriodStartDate}
                    onChange={(e) => setPayPeriodStartDate(e.target.value)}
                    style={{
                        padding: "8px",
                        fontSize: "1rem",
                        width: "100%",
                        maxWidth: "300px",
                        marginBottom: "20px",
                        backgroundColor: "var(--surface-2)",
                    }}
                />
            </div>
            <div className="pay-end">
                <label htmlFor="payPeriodEndDate" className="past-label">
                    Last Pay Date of Current Year:
                </label>
                <input
                    id="payPeriodEndDate"
                    type="date"
                    value={payPeriodEndDate}
                    onChange={(e) => setPayPeriodEndDate(e.target.value)}
                    style={{
                        padding: "8px",
                        fontSize: "1rem",
                        width: "100%",
                        maxWidth: "300px",
                        marginBottom: "20px",
                        backgroundColor: "var(--surface-2)",
                    }}
                />
            </div>
            <div className="button-container">
            <button className="submit-past"
                onClick={handleFetchPayroll}
            >
                Submit Pay Period
            </button>
            <button className="cancel-past"
                onClick={() => navigate(`/dashboard/business/${businessId}/start-payroll`)}
            >
                Cancel
            </button>
            </div>

            {employees.length > 0 && (
                <div className="quarter-map">
                    <h3>Employees</h3>
                    {employees.map((employee, employeeIndex) => (
                        <div
                            key={employeeIndex}
                            style={{
                                marginBottom: "30px",
                                textAlign: "left",
                                borderBottom: "1px solid #ddd",
                                paddingBottom: "20px",
                            }}
                        >
                            <p>
                                <strong>Name:</strong> {employee.first_name} {employee.last_name}
                            </p>
                            <p>
                                <strong>Hire Date:</strong>{" "}
                                {new Date(employee.hire_date).toLocaleDateString()}
                            </p>
                            <h4>Applicable Quarters:</h4>
                            {employee.quarters.map((quarter, quarterIndex) => (
                                <div key={quarterIndex} className="quarter-index">
                                    <p>
                                        <strong style={{color:"var(--text)"}}>{quarter}:</strong>
                                        <button
                                            style={{
                                                marginLeft: "10px",
                                                backgroundColor: "#3498db",
                                                color: "white",
                                                padding: "5px 10px",
                                                fontSize: "0.8rem",
                                                border: "none",
                                                borderRadius: "4px",
                                                cursor: "pointer",
                                            }}
                                            onClick={() => toggleQuarter(employeeIndex, quarterIndex)}
                                        >
                                            {expandedQuarters[`${employeeIndex}-${quarterIndex}`]
                                                ? "Hide"
                                                : "Records Information"}
                                        </button>
                                    </p>
                                    <div className="input-quarter">
                                        <div>
                                            <label>Gross Pay</label>
                                            <input
                                                id={`grossPay-${employee.id}-${quarterIndex}`}
                                                type="number"
                                                style={{ padding: "3px", marginLeft: "5px", width: "80%" }}
                                            />
                                        </div>
                                        <div>
                                            <label>Federal Tax</label>
                                            <input
                                                id={`federalTax-${employee.id}-${quarterIndex}`}
                                                type="number"
                                                style={{ padding: "3px", marginLeft: "5px", width: "80%" }}
                                            />
                                        </div>
                                        {employee.tax_inputs.map((field, fieldIndex) => (
                                            <div key={fieldIndex} className="emp-tax">
                                                <label htmlFor={`tax-${employee.id}-${quarterIndex}-${fieldIndex}`}>
                                                    {field.label} ({field.type === "state" ? "State" : "Local"}):
                                                </label>
                                                <input
                                                    id={`tax-${employee.id}-${quarterIndex}-${fieldIndex}`}
                                                    type="number"
                                                    placeholder={`Enter ${field.label}`}
                                                    style={{ padding: "5px", marginLeft: "5px" }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    {expandedQuarters[`${employeeIndex}-${quarterIndex}`] && (
                                        <div>
                                            <h5>Additional Details:</h5>
                                            <div
                                                className="add-details"
                                            >
                                                <div>
                                                    <label>401k Contribution</label>
                                                    <input
                                                        id={`contrib_401k-${employee.id}-${quarterIndex}`}
                                                        type="number"
                                                        style={{
                                                            padding: "3px",
                                                            marginLeft: "5px",
                                                            width: "80%",
                                                        }}
                                                    />
                                                </div>
                                                <div>
                                                    <label>Vacation Hours</label>
                                                    <input
                                                        id={`vacation_hours-${employee.id}-${quarterIndex}`}
                                                        type="number"
                                                        style={{
                                                            padding: "3px",
                                                            marginLeft: "5px",
                                                            width: "80%",
                                                        }}
                                                    />
                                                </div>
                                                <div>
                                                    <label>Vacation Pay</label>
                                                    <input
                                                        id={`vacation_pay-${employee.id}-${quarterIndex}`}
                                                        type="number"
                                                        style={{
                                                            padding: "3px",
                                                            marginLeft: "5px",
                                                            width: "80%",
                                                        }}
                                                    />
                                                </div>
                                                <div>
                                                    <label>Healthcare</label>
                                                    <input
                                                        id={`healthcare-${employee.id}-${quarterIndex}`}
                                                        type="number"
                                                        style={{
                                                            padding: "3px",
                                                            marginLeft: "5px",
                                                            width: "80%",
                                                        }}
                                                    />
                                                </div>
                                                <div>
                                                    <label>Dental</label>
                                                    <input
                                                        id={`dental-${employee.id}-${quarterIndex}`}
                                                        type="number"
                                                        style={{
                                                            padding: "3px",
                                                            marginLeft: "5px",
                                                            width: "80%",
                                                        }}
                                                    />
                                                </div>
                                                <div>
                                                    <label>Vision</label>
                                                    <input
                                                        id={`vision-${employee.id}-${quarterIndex}`}
                                                        type="number"
                                                        style={{
                                                            padding: "3px",
                                                            marginLeft: "5px",
                                                            width: "80%",
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ))}
                
                <button
                style={{
                    marginLeft: "550px",
                    marginBottom: "30px",
                    backgroundColor: "#3498db",
                    color: "white",
                    padding: "10px 15px",
                    fontSize: "1rem",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",

                }}
                onClick={handleSubmit}
            >
                Submit Past Data
            </button>
            </div>
            )}
            
        </div>
    );
};

export default PastPayroll;





